import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api, adminApi } from "@/lib/api";
import { useAdminAuth, useAuth } from "@/context/auth";

const qs = (o) => {
  const p = new URLSearchParams();
  Object.entries(o).forEach(([k, v]) => v && p.set(k, v === true ? "true" : v));
  const s = p.toString();
  return s ? `?${s}` : "";
};
const useGet = (key, path, opts = {}) => useQuery({ queryKey: key, queryFn: () => api.get(path), ...opts });
const useMut = (fn, keys) => {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: (_d, v) => (typeof keys === "function" ? keys(v) : keys).forEach((k) => qc.invalidateQueries({ queryKey: k })) });
};

export const useCategories = () => useGet(["categories"], "/categories", { staleTime: 3600000 });
export const useCategoryCounts = () => useGet(["categoryCounts"], "/categories/counts");
export const useContacts = (f = {}) => useGet(["contacts", f], `/contacts${qs({ search: f.search, categoryId: f.categoryId, favorite: f.favorite, sort: f.sort })}`);
export const useContact = (id) => useGet(["contact", id], `/contacts/${id}`, { enabled: !!id });
const CONTACT_KEYS = [["contacts"], ["categoryCounts"], ["explore"]];
export const useUpdateContact = (id) => useMut((patch) => api.patch(`/contacts/${id}`, patch), [["contact", id], ...CONTACT_KEYS]);
export const useDeleteContact = () => useMut((id) => api.del(`/contacts/${id}`), CONTACT_KEYS);
export const useExplore = (f) => useGet(["explore", f], `/explore/contacts${qs(f)}`);

export const useEvents = (f = {}) => useGet(["events", f], `/events${qs(f)}`);
export const useEvent = (id) => useGet(["event", id], `/events/${id}`, { enabled: !!id });
export const useEventContacts = (id) => useGet(["eventContacts", id], `/events/${id}/contacts`, { enabled: !!id });
export const useContactEvents = (id) => useGet(["contactEvents", id], `/contacts/${id}/events`, { enabled: !!id });
export const useCreateEvent = () => useMut((b) => api.post("/events", b), [["events"]]);
export const useUpdateEvent = (id) => useMut((b) => api.patch(`/events/${id}`, b), [["events"], ["event", id]]);
export const useDeleteEvent = () => useMut((id) => api.del(`/events/${id}`), [["events"]]);
const linkKeys = (v) => [["events"], ["event", v.eventId], ["eventContacts", v.eventId], ["contactEvents", v.contactId]];
export const useAddToEvent = () => useMut(({ eventId, contactId }) => api.post(`/events/${eventId}/contacts/${contactId}`), linkKeys);
export const useRemoveFromEvent = () => useMut(({ eventId, contactId }) => api.del(`/events/${eventId}/contacts/${contactId}`), linkKeys);

const SEL_KEY = "cardbox.selectedEventId";
export function useSelectedEvent() {
  const [id, setId] = useState(() => localStorage.getItem(SEL_KEY));
  const set = (v) => {
    if (v) localStorage.setItem(SEL_KEY, v);
    else localStorage.removeItem(SEL_KEY);
    setId(v);
  };
  return [id, set];
}

export const useGoals = () => useGet(["goals"], "/goals");
export const useGoalHistory = () => useGet(["goalHistory"], "/goals/history");
export const useCompleteGoal = () => useMut((key) => api.post("/goals/complete", { key }), [["goals"], ["goalHistory"]]);
export const useCreateGoal = () => useMut((b) => api.post("/goals", b), [["goals"]]);
export const useDeleteGoal = () => useMut((id) => api.del(`/goals/${id}`), [["goals"]]);

export const useNotifications = () => useGet(["notifications"], "/notifications");
export const useUnread = () => useGet(["notificationsUnread"], "/notifications/unread-count", { refetchInterval: 30000 });
const N_KEYS = [["notifications"], ["notificationsUnread"]];
export const useMarkAllRead = () => useMut(() => api.post("/notifications/read-all", {}), N_KEYS);
export const useClearNotifications = () => useMut(() => api.del("/notifications"), N_KEYS);
export const useOpenNotification = () => useMutation({ mutationFn: (id) => api.post(`/notifications/${id}/open`, {}) });
export const useNotifPrefs = () => useGet(["notificationPrefs"], "/users/me/notification-prefs");
export function useUpdateNotifPrefs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch) => api.patch("/users/me/notification-prefs", patch),
    onMutate: (patch) => {
      const prev = qc.getQueryData(["notificationPrefs"]);
      if (prev) qc.setQueryData(["notificationPrefs"], { ...prev, ...patch });
      return { prev };
    },
    onError: (_e, _p, ctx) => ctx?.prev && qc.setQueryData(["notificationPrefs"], ctx.prev),
    onSuccess: (d) => qc.setQueryData(["notificationPrefs"], d),
  });
}

export const useReminders = (filter = "all") => useGet(["reminders", filter], `/reminders?filter=${filter}`);
export const useDueCount = () => useGet(["remindersDueCount"], "/reminders/due-count");
export const useContactReminder = (id) => useGet(["contactReminder", id], `/contacts/${id}/reminder`, { enabled: !!id });
const rKeys = (id) => [["reminders"], ["remindersDueCount"], ["goals"], ["contactReminder", id]];
export const useSaveReminder = (contactId) => useMut((b) => api.post("/reminders", { contactId, dueAt: b.dueAt, note: b.note ?? null }), rKeys(contactId));
export const useCompleteReminder = (contactId) => useMut((id) => api.post(`/reminders/${id}/complete`, {}), rKeys(contactId));
export const useDeleteReminder = (contactId) => useMut((id) => api.del(`/reminders/${id}`), rKeys(contactId));

export function useScanUsage() {
  const { user } = useAuth();
  return useGet(["scanUsage", user?.id], "/scans/usage", { enabled: !!user, staleTime: 0 });
}
export function useSubscription() {
  const { user } = useAuth();
  return useGet(["subscription", user?.id], "/subscriptions/me", { enabled: !!user, staleTime: 0 });
}
export function useMe() {
  const { user } = useAuth();
  return useGet(["me", user?.id], "/auth/me", { enabled: !!user, staleTime: 0, retry: false });
}
export const usePaymentConfig = () => useGet(["paymentConfig"], "/payments/config", { staleTime: 300000 });
export const usePromoBanner = () => useGet(["promoBanner"], "/promos/banner", { staleTime: 300000 });
export function useFileToken() {
  const { token } = useAuth();
  return useQuery({ queryKey: ["fileToken"], queryFn: async () => (await api.get("/files/token")).token, enabled: !!token, staleTime: 1200000, refetchInterval: 1200000 }).data ?? null;
}

export function useAdminQuery(path, opts = {}) {
  const { admin } = useAdminAuth();
  return useQuery({ queryKey: ["admin", admin?.id, path], queryFn: () => adminApi.get(path), enabled: !!admin, staleTime: 0, retry: false, ...opts });
}
export function useAdminInvalidate() {
  const qc = useQueryClient();
  return () => ["admin", "paymentConfig", "promoBanner", "scanUsage", "subscription"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
}
